/**
 * AI Agent logic for the Wellness AI web app.
 * Now powered by Advanced RAG and Master Wellness Agent for world-class wellness support.
 */

import { createMasterTherapistAgent } from '../services/masterTherapistAgent';

class AIAgent {
  constructor() {
    this.masterAgent = null;
    this.gender = 'male'; // Default gender
    this.userName = '';
    this.partnerName = '';
    this.userId = null;
    this.initialized = false;
  }

  async initialize(userId = null) {
    this.userId = userId;
    this.masterAgent = createMasterTherapistAgent(userId);
    await this.masterAgent.initialize(userId);
    this.initialized = true;
  }

  async setGender(gender) {
    if (gender === 'male' || gender === 'female') {
      this.gender = gender;
      if (this.masterAgent) {
        this.masterAgent.setGender(gender);
      }
    }
  }

  getGender() {
    return this.gender;
  }

  async setNames(userName, partnerName) {
    if (userName) this.userName = userName;
    if (partnerName) this.partnerName = partnerName;
    
    if (this.masterAgent) {
      this.masterAgent.setNames(userName, partnerName);
    }
  }

  async processInput(userInput) {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const response = await this.masterAgent.processInput(userInput);
      return response;
    } catch (error) {
      console.error('Error in AI Agent processInput:', error);
      const fallbackMessage = "Hey, I'm having a little technical trouble right now. Could you try that again in a moment?";
      return fallbackMessage;
    }
  }

  clearConversation() {
    if (this.masterAgent) {
      this.masterAgent.clearConversation();
    }
  }

  getConversationHistory() {
    if (this.masterAgent) {
      return this.masterAgent.getConversationHistory();
    }
    return [];
  }

  setConversationHistory(history) {
    if (this.masterAgent) {
      this.masterAgent.setConversationHistory(history);
    }
  }

  async getIntroduction() {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const introduction = await this.masterAgent.getIntroduction();
      return introduction;
    } catch (error) {
      console.error('Error fetching introduction:', error);
      return `Hello. I'm ${this.gender === 'female' ? 'Sarah' : 'Michael'}. I seem to be having a slight issue starting up, but I'm here to help.`;
    }
  }

  // Enhanced methods for accessing RAG insights (optional for UI)
  getLastRAGAnalysis() {
    if (this.masterAgent) {
      return this.masterAgent.getLastRAGAnalysis();
    }
    return null;
  }

  getSessionInsights() {
    if (this.masterAgent) {
      return this.masterAgent.getSessionInsights();
    }
    return null;
  }

  getTherapeuticMethodsUsed() {
    if (this.masterAgent) {
      return this.masterAgent.getTherapeuticMethodsUsed();
    }
    return {};
  }
}

// Export a factory function to create the AI agent
export const createAIAgent = () => {
  return new AIAgent();
};
